import { scoreColor } from '../../shared/designTokens.js';
import { ScoreBar } from './ScoreBar.jsx';
import { SeverityBadge } from './SeverityBadge.jsx';
import { severityFromScore } from '../utils/severity.js';
import { getCategorySummary } from '../../engine/categorySummary.js';
import { STATUS_LABEL } from '../../shared/checkDisplay.js';
import { isScoreNeutralCategory } from '../../shared/baselineComparisonDisplay.js';

/**
 * Category row inside the scores accordion. Clicking toggles the row open
 * via the `onToggle` handler; the accordion shell handles single-open
 * behavior so this component stays dumb.
 */
export function CategoryRow({ category, even, open, onToggle }) {
  const scoreNeutral = isScoreNeutralCategory(category);
  // STAGE4-FIX-B (parity): the summary is THE engine formatter the PDF
  // category-scores table renders (getCategorySummary) — one builder, two
  // mirrors. The old dashboard-local composer ("N checks — all within
  // threshold.") produced a different string from the PDF for every category.
  const summary = getCategorySummary(category);
  const rowClassName = `cat-row ${even ? 'even' : ''} ${open ? 'is-open' : ''}`;
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
  };

  // BASELINE-SUB4: score-neutral comparison category — no numeric score, no
  // ScoreBar, no SeverityBadge (they would render a red "0" / NaN). A neutral
  // COMPARISON pill spans the score/bar/badge columns instead.
  if (scoreNeutral) {
    return (
      <div
        className={rowClassName}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={`cat-panel-${category.id}`}
      >
        <div>
          <div className="cat-name">{category.name}</div>
          <div className="cat-summary">{summary}</div>
        </div>
        {/* A3 (D-DashCatWeight): the nominal weight the PDF category-scores page
            shows (originalWeight, engine-emitted) — rendered for the comparison
            row too, matching the PDF (its weight cell is unconditional). */}
        <div className="cat-weight">
          <span className="cat-weight-label">Weight</span>{category.originalWeight ?? 0}%
        </div>
        <div className="cat-comparison-cell" style={{ gridColumn: '3 / 6' }}>
          {/* FIX-B Part 2: shared §A3 label — same bytes as the PDF pill. */}
          <span className="cat-comparison-pill">{STATUS_LABEL.COMPARISON}</span>
        </div>
        <div className={`cat-arrow ${open ? 'open' : ''}`} aria-hidden="true">▾</div>
      </div>
    );
  }

  const severity = severityFromScore(category.score);
  const rounded = Math.round(category.score);
  return (
    <div
      className={rowClassName}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      aria-controls={`cat-panel-${category.id}`}
    >
      <div>
        <div className="cat-name">{category.name}</div>
        <div className="cat-summary">{summary}</div>
      </div>
      {/* A3 (D-DashCatWeight): the PDF's WEIGHT column value (originalWeight),
          render-only from the engine emission. */}
      <div className="cat-weight">
        <span className="cat-weight-label">Weight</span>{category.originalWeight ?? 0}%
      </div>
      <div className="cat-score" style={{ color: scoreColor(category.score) }}>
        {rounded}
      </div>
      <div>
        <ScoreBar score={category.score} />
      </div>
      <div>
        <SeverityBadge severity={severity} />
      </div>
      <div className={`cat-arrow ${open ? 'open' : ''}`} aria-hidden="true">▾</div>
    </div>
  );
}
